import {
  ActionBar,
  addCountryObject,
  addonIsOnBlacklist,
  AddressForm,
  apiErrorsContain,
  backendApiClient,
  CancelButton,
  checkAddonBlacklist,
  Column,
  CONFIGURATION,
  ConfirmationModal,
  Container,
  countryShape,
  Details,
  DetailsItem,
  htmlFormat,
  htmlLink,
  isEmpty,
  isNotEmpty,
  isSomething,
  Loader,
  RadioSelect,
  Row,
  SubmitButton,
  url,
  useApi,
  useBackend,
  useBrowser,
  useConfiguration,
  useNotifier,
  useTranslateError,
  useUser,
} from "lcm-iot-commons";
import { FormattedMessage, useIntl } from "react-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { extractConnectSubscription } from "../../extractors/connectSubscriptionsExtractor";
import ClientApplicationForm from "../ClientApplications/ClientApplicationForm";
import { extractPlanCart } from "../../extractors/planCartExtractor";
import {
  IConnectSubscription,
  IConnectSubscriptionsResponse,
} from "../../shapes/subscriptionsResponseShape";
import { planCartDataShape } from "../../shapes/planCartShape";
import { packPlanCartCheckout } from "../../packmans/planCartPackman";

export function ConnectSubscriptionCreate() {
  const api = useApi();
  const backend = useBackend();
  const browser = useBrowser();
  const configuration = useConfiguration();
  const intl = useIntl();
  const notifier = useNotifier();
  const translateError = useTranslateError();
  const user = useUser();
  const navigate = useNavigate();

  const clientApplicationFormRef = useRef<HTMLFormElement | null>(null);
  const billingAddressFormRef = useRef<HTMLFormElement | null>(null);
  const shippingAddressFormRef = useRef<HTMLFormElement | null>(null);

  const [targetUrl, setTargetUrl] = useState("/plan_variants");
  const [addonsNotAllowed, setAddonsNotAllowed] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [planCart, setPlanCart] = useState<planCartDataShape>();
  const [isShippingDifferent, setIsShippingDifferent] = useState(false);

  const { data: countries } = backend.get.useQuery("/countries", {
    all: false,
    language: configuration.language,
  });
  const { data: existingClientApplications } = api.get.useQuery(
    "/api_subscriptions",
    null,
    {
      select: (response: IConnectSubscriptionsResponse) =>
        response.api_subscriptions.map(
          (subscription: IConnectSubscription) =>
            subscription.client_application.name,
        ),
    },
  );
  const { data: planCartResponse, isLoading: isLoadingPlanCart } =
    backend.get.useQuery(
      "/plan_cart",
      { language: configuration.language },
      {
        select: extractPlanCart,
        cacheTime: 0,
      },
    );
  const { data: subscriptionData } = api.get.useQuery(
    `/api_subscriptions/${(planCartResponse as planCartDataShape)?.subscriptionId}`,
    null,
    {
      enabled: !!(planCartResponse as planCartDataShape)?.subscriptionId,
      select: extractConnectSubscription,
    },
  );

  const checkoutPlanCart = async (params: $TSFixMe) =>
    backendApiClient().post(
      "/plan_cart/checkout",
      { language: configuration.language },
      params,
    );

  const updatePlanCart = async (params: $TSFixMe) =>
    backendApiClient().patch("/plan_cart", params);

  useEffect(() => {
    if (planCartResponse && !planCart) {
      const planCartData = {
        ...planCartResponse,
        billingAddress:
          addCountryObject(
            (planCartResponse as planCartDataShape).billingAddress,
            countries,
          ) || {},
        shippingAddress: addCountryObject(
          (planCartResponse as planCartDataShape).shippingAddress,
          countries,
        ),
      };

      setPlanCart(planCartData as planCartDataShape);
      setAddonsNotAllowed(checkAddonBlacklist(planCartResponse));
      setIsShippingDifferent(isSomething(planCartData.shippingAddress));
    }

    /* istanbul ignore next */
    if (planCart && user?.billingEmail) {
      setPlanCart((prevCart) =>
        prevCart
          ? {
              ...prevCart,
              billingAddress: {
                ...prevCart.billingAddress,
                email: user.billingEmail,
              },
            }
          : prevCart,
      );
    }

    // Handle navigation logic
    if (planCart && subscriptionData?.status !== "open") {
      if (
        subscriptionData?.status &&
        subscriptionData.status === "confirmed" &&
        subscriptionData.subscriptionName === "Basic"
      ) {
        setTargetUrl(
          `/subscriptions/connect/${planCart.subscriptionId}/upgrade`,
        );
      } else {
        setTargetUrl(`/subscriptions/connect/${planCart.subscriptionId}`);
      }
    }
  }, [planCartResponse, subscriptionData]);

  const radioSelectOptions = useMemo(
    () =>
      configuration?.disablePaymentGateway
        ? /* istanbul ignore next */ [
            {
              value: "off",
              label: intl.formatMessage({
                id: "subscription.payment_method.invoice",
              }),
            },
          ]
        : [
            {
              value: "on",
              label: intl.formatMessage({
                id: "subscription.payment_method.credit_card",
              }),
            },
            {
              value: "off",
              label: intl.formatMessage({
                id: "subscription.payment_method.invoice",
              }),
            },
          ],
    [configuration?.disablePaymentGateway, intl],
  );

  const mtext = (
    <div>
      <FormattedMessage
        id="subscription_create.addon_country_modal_dialog.message"
        values={{
          ...htmlFormat,
          notAllowedAddons: addonsNotAllowed.join(", "),
          // @ts-expect-error TS(2322) FIXME: Type '(...chunks: any[]) => Element' is not assign... Remove this comment to see the full error message
          a: htmlLink({
            href: CONFIGURATION.HELP_URL_AVAILABILITY,
            target: "_blank",
          }),
        }}
      />
    </div>
  );

  const onConfirmModal = () => {
    setShowModal(false);
    navigate(url(targetUrl));
  };

  const onCloseModal = () => {
    setShowModal(false);
  };

  const addonModal =
    showModal && !isLoadingPlanCart ? (
      <ConfirmationModal
        id="addon-country-not-allowed-modal-dialog"
        show={showModal}
        titleText={intl.formatMessage({
          id: "subscription_create.addon_country_modal_dialog.title",
        })}
        messageText={mtext}
        buttonConfirmText={intl.formatMessage({
          id: "subscription_create.addon_country_modal_dialog.button_close",
        })}
        buttonCancelText={intl.formatMessage({
          id: "subscription_create.addon_country_modal_dialog.button_confirm",
        })}
        onConfirm={onConfirmModal}
        onClose={onCloseModal}
      />
    ) : null;

  const byInvoiceHint = (
    <div className="space-after">
      {intl.formatMessage({
        id: "subscription.payment_method.by_invoice_hint",
      })}
    </div>
  );

  const addonBillingCountryAllowed = useCallback(
    (country: countryShape) => {
      let newAddonsNotAllowed = addonsNotAllowed;
      /* istanbul ignore else */
      if (!isShippingDifferent) {
        newAddonsNotAllowed = addonIsOnBlacklist(
          planCart?.addons,
          country?.code,
        );
      }

      setAddonsNotAllowed(newAddonsNotAllowed);
      setShowModal(newAddonsNotAllowed.length > 0);
      setPlanCart((prev) => ({
        ...prev,
        billingAddress: { country },
      }));
    },
    [addonsNotAllowed, isShippingDifferent, planCart?.addons],
  );

  const addonShippingCountryAllowed = useCallback(
    (country: countryShape) => {
      let newAddonsNotAllowed = addonsNotAllowed;
      newAddonsNotAllowed = addonIsOnBlacklist(planCart?.addons, country?.code);
      setAddonsNotAllowed(newAddonsNotAllowed);
      setShowModal(newAddonsNotAllowed.length > 0);
      setPlanCart((prev) => ({
        ...prev,
        shippingAddress: {
          country,
        },
      }));
    },
    [addonsNotAllowed, planCart?.addons],
  );

  const resetFormSubmissions = (paid: boolean) => {
    const resetForm = (ref: React.RefObject<HTMLFormElement>) =>
      ref.current?.setSubmitting(false);

    resetForm(clientApplicationFormRef);

    /* istanbul ignore else */
    if (paid) {
      resetForm(billingAddressFormRef);
      /* istanbul ignore else */
      if (isShippingDifferent) {
        resetForm(shippingAddressFormRef);
      }
    }
  };

  const handleAddressFormErrors = (apiErrors: $TSFixMe) => {
    const paid =
      planCart?.planVariant?.type === "paid" ||
      /* istanbul ignore next */ isNotEmpty(planCart?.addons);

    setIsSubmitting(false);

    /* istanbul ignore next 14 */
    if (apiErrorsContain(apiErrors, "taken")) {
      clientApplicationFormRef.current?.setErrors({
        name: intl.formatMessage({ id: "api.error.client_application.taken" }),
      });
      // @ts-expect-error TS(2345) FIXME: Argument of type '"tax_address"' is not assignable... Remove this comment to see the full error message
    } else if (apiErrorsContain(apiErrors, "invalid_input", "tax_address")) {
      notifier.showError(
        intl.formatMessage({ id: "api.error.tax_address.invalid_input" }),
      );
      // @ts-expect-error TS(2345) FIXME: Argument of type '"redirect_uris"' is not assignable... Remove this comment to see the full error message
    } else if (apiErrorsContain(apiErrors, "invalid_input", "redirect_uris")) {
      clientApplicationFormRef.current?.setErrors({
        redirectURIs: intl.formatMessage({
          id: "api.error.redirect_uri.invalid_input",
        }),
      });
    } else if (
      // @ts-expect-error TS(2345) FIXME: Argument of type '"billing_address_zip_code"' is n... Remove this comment to see the full error message
      apiErrorsContain(apiErrors, "invalid_input", "billing_address_zip_code")
    ) {
      billingAddressFormRef.current?.setErrors({
        zipCode: intl.formatMessage({
          id: "api.error.billing_address_zip_code.invalid_input",
        }),
      });
    } else if (
      // @ts-expect-error TS(2345) FIXME: Argument of type '"shipping_address_zip_code"' is ... Remove this comment to see the full error message
      apiErrorsContain(apiErrors, "invalid_input", "shipping_address_zip_code")
    ) {
      shippingAddressFormRef.current?.setErrors({
        zipCode: intl.formatMessage({
          id: "api.error.shipping_address_zip_code.invalid_input",
        }),
      });
    } else if (
      // @ts-expect-error TS(2345) FIXME: Argument of type '"billing_address.base"' is not a... Remove this comment to see the full error message
      apiErrorsContain(apiErrors, "vat_number", "billing_address.base") ||
      // @ts-expect-error TS(2345) FIXME: Argument of type '"billing_address_vat_number"' is... Remove this comment to see the full error message
      apiErrorsContain(apiErrors, "invalid_input", "billing_address_vat_number")
    ) {
      billingAddressFormRef.current?.setErrors({
        vatNumber: intl.formatMessage({
          id: "validation.vat_number.mandatory",
        }),
      });
    } else {
      notifier.showError(translateError(apiErrors));
    }

    clientApplicationFormRef.current?.setSubmitting(false);

    /* istanbul ignore else */
    if (paid) {
      billingAddressFormRef.current?.setSubmitting(false);
      /* istanbul ignore else */
      if (isShippingDifferent) {
        shippingAddressFormRef.current?.setSubmitting(false);
      }
    }
  };

  // Helper function to extract form values
  const extractFormValues = (paid: boolean) => {
    const getFormValues = (ref: React.RefObject<HTMLFormElement>) =>
      ref.current?.values();

    return {
      clientAppFormValues:
        clientApplicationFormRef.current?.values() ||
        /* istanbul ignore next */ planCart?.clientApplication,
      autoCollection: (paid && planCart?.autoCollection) || "on",
      billingAddressFormValues: paid
        ? getFormValues(billingAddressFormRef)
        : undefined,
      shippingAddressFormValues:
        paid && isShippingDifferent
          ? getFormValues(shippingAddressFormRef)
          : undefined,
    };
  };

  const handleOnSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();

    const isPaid =
      planCart?.planVariant?.type === "paid" || isNotEmpty(planCart?.addons);

    const clientAppCheck =
      clientApplicationFormRef.current?.submit() ||
      /* istanbul ignore next */ (!clientApplicationFormRef.current &&
        planCart?.clientApplication);

    const isClientAppValid =
      clientAppCheck &&
      (!isPaid || billingAddressFormRef.current?.submit()) &&
      (!isShippingDifferent || shippingAddressFormRef.current?.submit());

    if (!isClientAppValid) {
      resetFormSubmissions(isPaid);
      return;
    }

    setIsSubmitting(true);

    // Extract form values
    const formValues = extractFormValues(isPaid);

    if (formValues.autoCollection === "on") {
      await handleCreditCardPayment(formValues);
    } else {
      await handleInvoicePayment(formValues);
    }
  };

  // Helper function to handle credit card payment flow
  const handleCreditCardPayment = async (
    formValues: ReturnType<typeof extractFormValues>,
  ) => {
    try {
      const planCartParams = packPlanCartCheckout(
        formValues.clientAppFormValues,
        formValues.autoCollection,
        formValues.billingAddressFormValues,
        formValues.shippingAddressFormValues,
      );

      const nextPlanCartResponse = await checkoutPlanCart(planCartParams);

      /* istanbul ignore else */
      if (nextPlanCartResponse) {
        const nextPlanCart = extractPlanCart(nextPlanCartResponse);
        const isFreePlan =
          nextPlanCart.planVariant?.type === "free" &&
          isEmpty(planCart?.addons);

        if (isFreePlan) {
          notifier.showSuccess(
            intl.formatMessage({
              id: "subscription_create.success_notification_free",
            }),
          );
          navigate(
            url(`/subscriptions/connect/${nextPlanCart.subscriptionId}`),
          );
        } else {
          browser.redirectToExtern(nextPlanCart.checkoutUrl);
        }
      }
    } catch (error) {
      handleAddressFormErrors(error);
    }
  };

  // Helper function to handle invoice payment flow
  const handleInvoicePayment = async (
    formValues: ReturnType<typeof extractFormValues>,
  ) => {
    try {
      const planCartParams = packPlanCartCheckout(
        formValues.clientAppFormValues,
        formValues.autoCollection,
        formValues.billingAddressFormValues,
        formValues.shippingAddressFormValues,
      );

      await updatePlanCart(planCartParams);
      navigate(url("/subscriptions/connect/new/overview"));
    } catch (error) {
      /* istanbul ignore next */
      handleAddressFormErrors(error);
    }
  };

  const handleOnPaymentMethodChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPlanCart((prev) => ({
        ...prev,
        autoCollection: event.target.value,
      }));
    },
    [],
  );

  const handleOnShippingDifferentChange = useCallback(() => {
    setIsShippingDifferent((prev) => {
      const isShippingDifferentChecked = !prev;
      const newAddonsNotAllowed = checkAddonBlacklist(
        planCart,
        isShippingDifferentChecked,
      );

      setAddonsNotAllowed(newAddonsNotAllowed);
      setShowModal(newAddonsNotAllowed.length > 0);

      return isShippingDifferentChecked;
    });
  }, [planCart]);

  return (
    <Container id="connect-subscription-create">
      <Row>
        <Column md={7}>
          {!isLoadingPlanCart && planCart ? (
            <div>
              <ActionBar>
                <h1>
                  <FormattedMessage id="subscription_create.application_title" />
                </h1>
              </ActionBar>

              {planCart.clientApplication ? (
                <Details>
                  <DetailsItem
                    id="subscription-client-application-name"
                    translationKey="label.client_application"
                    value={planCart.clientApplication.name}
                  />
                  <DetailsItem
                    id="subscription-client-application-description"
                    translationKey="label.description"
                    value={planCart.clientApplication.description}
                  />
                </Details>
              ) : (
                <ClientApplicationForm
                  ref={clientApplicationFormRef}
                  initialValues={planCart.clientApplication ?? {}}
                  takenNames={existingClientApplications as string[]}
                />
              )}

              {planCart?.planVariant?.type === "paid" ||
              isNotEmpty(planCart.addons) ? (
                <div>
                  <ActionBar>
                    <h1 id="subscription-payment-method-header">
                      <FormattedMessage id="subscription.payment_method.header" />
                    </h1>
                  </ActionBar>
                  <RadioSelect
                    id="payment-options"
                    options={radioSelectOptions}
                    // @ts-expect-error TS(2322) FIXME: Type '{ id: string; options: { value: string;
                    handleChange={handleOnPaymentMethodChange}
                    values={{
                      paymentOptions: planCart.autoCollection,
                    }}
                    name="paymentOptions"
                    handleBlur={() => true}
                  />
                  {byInvoiceHint}
                  <ActionBar>
                    <h1 id="subscription-billing-address-header">
                      <FormattedMessage id="subscription.billing_address.header" />
                    </h1>
                  </ActionBar>
                  <AddressForm
                    id="billing-address"
                    initialValues={{
                      ...planCart.billingAddress,
                      customerPurchaseOrder: planCart.customerPurchaseOrder,
                    }}
                    onSubmit={handleOnSubmit}
                    countries={
                      (countries as { countries: countryShape[] }).countries
                    }
                    prefix="billingAddress"
                    ref={billingAddressFormRef}
                    showEmail
                    showVat
                    showCustomerPurchaseOrder
                    countryChangeAction={addonBillingCountryAllowed}
                    firstLastNameRequired
                  />
                  <div className="space-after">
                    <input
                      id="addressdifferent-checkbox"
                      type="checkbox"
                      className="unchecked"
                      disabled={isSubmitting}
                      value="false"
                      onChange={handleOnShippingDifferentChange}
                      checked={isShippingDifferent}
                    />
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label htmlFor="addressdifferent-checkbox">
                      <FormattedMessage id="subscription.addresses_different" />
                    </label>
                  </div>
                  {isShippingDifferent ? (
                    <div className="space-after">
                      <ActionBar>
                        <h1 id="subscription-shipping-address-header">
                          <FormattedMessage id="subscription.shipping_address.header" />
                        </h1>
                      </ActionBar>
                      <AddressForm
                        id="shipping-address"
                        initialValues={planCart.shippingAddress ?? {}}
                        onSubmit={handleOnSubmit}
                        countries={
                          (countries as { countries: countryShape[] }).countries
                        }
                        prefix="shippingAddress"
                        ref={shippingAddressFormRef}
                        countryChangeAction={addonShippingCountryAllowed}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="btn-group">
                <SubmitButton
                  id="checkout-subscription-submit"
                  text={intl.formatMessage({ id: "label.continue" })}
                  onClick={handleOnSubmit}
                  fetching={isSubmitting}
                  disabled={addonsNotAllowed.length > 0}
                />
                <CancelButton
                  id="checkout-subscription-cancel"
                  // @ts-expect-error TS(2322) FIXME: Type '{ id: string; disabled: any; target: any; }'... Remove this comment to see the full error message
                  disabled={isSubmitting}
                  target={targetUrl}
                />
              </div>
            </div>
          ) : null}
          {addonModal}
          <Loader loading={isLoadingPlanCart} />
        </Column>
      </Row>
    </Container>
  );
}

export default ConnectSubscriptionCreate;
